/****************************************************
 * Module Name:  StoneAsOnDate.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Button, Container, Tooltip, Drawer, Typography, TextField, Badge, MenuItem, Menu } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import renderCellExpand from "../common/dataGridCellExpand";
import StoneDetailModal from "../common/stoneDetailModal";
import StockSearchCriteria from "../operations/stockSearchCriteria";
import Constants from "../../utility/constants";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import PacketHistoryModal from "../common/packetHistoryModal";
import NoPermission from "../common/noPermission";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TuneIcon from '@mui/icons-material/Tune';
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import colourchart from '../../images/ColorScheme.png'
import CommonStoneSelection from "../common/commonStoneSelection";
import StoneMediaModal from "../common/stoneMediaModal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";
import Download from "@mui/icons-material/Download";
import { createExcelFile } from "../common/createExcelFile";
import { TbTableExport } from "react-icons/tb";
import { LoadingButton } from "@mui/lab";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--NEW": {
        backgroundColor: "#F9F08D",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#F9F08D",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        backgroundColor: 'grey',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "& .super-app-theme--LAB": {
        backgroundColor: "#7EAEEE",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#7EAEEE",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
            color: "black",
        },
    },
    "& .super-app-theme--RE-LAB": {
        backgroundColor: "#FCCD91",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FCCD91",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--RE-CHECK": {
        backgroundColor: "#FFFFCC",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FFFFCC",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--RE-CHECK_SENT": {
        backgroundColor: "#B8ACF6",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#B8ACF6",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--RE-CHECK_DONE": {
        backgroundColor: "#388BFF",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#388BFF",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MIX": {
        backgroundColor: "#E0E88C",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#E0E88C",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--AVAILABLE_APPROVAL_PENDING": {
        backgroundColor: "#FBDEDE",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FBDEDE",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--AVAILABLE": {
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#B6B9D6",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#B6B9D6",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_OUT": {
        backgroundColor: "#CAF7F6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CAF7F6",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SELL": {
        backgroundColor: "#CCFF99",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CCFF99",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SELL_SENT": {
        backgroundColor: "#F797D2",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#F797D2",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SOLD": {
        backgroundColor: "#26D4B9",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#26D4B9",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SELL_CANCELLED": {
        backgroundColor: "#F59598",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#F59598",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },

}));

const StyledMenuButtons = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 100,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            fontSize: 15,
            padding: "4px 12px",
            '& .MuiSvgIcon-root': {
                fontSize: 16,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

const StoneAsOnDate = () => {

    const stoneStatusOptions = [
        { value: -1, label: " -- ANY -- " },
        { value: "AVAILABLE", label: "AVAILABLE" },
        { value: "HOLD", label: "HOLD" },
        { value: "MEMO_OUT", label: "MEMO_OUT" },
        { value: "RE-LAB", label: "RE-LAB" },
        { value: "RE-CHECK", label: "RE-CHECK" },
        { value: "NEW", label: "NEW" },
        { value: "LAB", label: "LAB" },
        { value: "AVAILABLE_APPROVAL_PENDING", label: "AVAILABLE_APPROVAL_PENDING" },
        { value: "MIX", label: "MIX" },
        { value: "SELL", label: "SELL" },
        { value: "SELL_SENT", label: "SELL_SENT" },
        { value: "SOLD", label: "SOLD" },
    ];

    const [stockData, setStockData] = useState([]);
    const [searchCriteria, setSearchCriteria] = useState("");
    const [stockLoader, setStockLoader] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [access, setAccess] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
    const [sortModel, setSortModel] = React.useState([]);
    const [columnNames, setColumnNames] = React.useState([]);
    const [stockCriteriaState, setStockCriteriaState] = useState('');
    const [drawerExpanded, setDrawerExpanded] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    const [stoneMediaURL, setStoneMediaURL] = useState([]);
    const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
    const [date, setDate] = useState();
    const [stoneStatus, setStoneStatus] = useState(stoneStatusOptions.filter(iStatus =>
        iStatus.value === "AVAILABLE" || iStatus.value === "HOLD" || iStatus.value === "MEMO_OUT"
    ));

    // eslint-disable-next-line
    const [fullExcelPermission, setFullExcelPermission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.DIRECT_EXCEL_DOWNLOAD_FULL
        )
    );

    const buttonMinWidth = Utility.isReadAllowed(fullExcelPermission) ? "130px" : "100px";
    const [anchorEl1, setAnchorEl1] = React.useState(null);
    const open1 = Boolean(anchorEl1);
    const handleClick1 = (event) => {
        setAnchorEl1(event.currentTarget);
    };
    const handleCloseButton1 = () => {
        setAnchorEl1(null);
    };

    useEffect(() => {
        PageTitle("REPORT_STOCK_AS_ON_DATE");
        checkPermission();
    }, []);

    useEffect(() => {
        if (selectionModel.length <= 0) {
            loadTotalData(stockData);
            return;
        }
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        stockData.forEach((iData) => {
            if (selectionModel.includes(iData.stoneid)) {
                let pricePerCarat = iData.price
                totalSize += iData.size;
                totalPrice += (iData.size * pricePerCarat);
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
            }
        });
        setNoOfSelectedStones(selectionModel.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );

    }, [selectionModel]);

    const loadTotalData = (responseData) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        responseData.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            if (iData.discount !== -100)
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
        });
        setNoOfSelectedStones(responseData.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );

    };

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.REPORT_STOCK_AS_ON_DATE
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const handleSearch = (data, stockCriteriaStored) => {
        if (!stockCriteriaStored)
            return;

        setSearchCriteria(data);
        setStockCriteriaState(stockCriteriaStored);
        setStockLoader(true);

        let tempDate = dayjs(date).format('YYYY-MM-DD') + "T23:59:59.999Z";
        if (!stoneStatus.find(iStatus => iStatus.value === -1)) {
            data.status = stoneStatus.map(iStatus => iStatus.value);
        }
        StoneProxy.GetStoneAsOnDate(tempDate, data, (responseData) => {
            if (responseData) {
                const distinctStatus = Utility.getDistinctOptions(responseData, 'status');
                prepareColumns(distinctStatus);
                setSortModel([]);
                setStockData(responseData);
                setStockLoader(false);
                loadTotalData(responseData);
            } else {
                setSortModel([]);
                setStockLoader(false);
                setErrorSnackbar(true);
            }
        });
    };

    const prepareColumns = (distinctStatus) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "status") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = distinctStatus;
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
            }
        });

        setColumnNames(columnNames);
    }

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setShowPacketDetailModal(false)
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handlePacketDetailModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowPacketDetailModal(true);
    };

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stockData, selectionModel);

        setStockData(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            cellClassName: 'stickyColumn',
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "e24542ae-7b98-44f2-82be-09dfa2dd850b": {
            field: "mumid",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        "7a913460-41e0-441d-85b9-1f33d71416fb": {
            field: "suratid",
            headerName: "REF NO",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
            cellClassName: (params) => `super-app-theme--${params.row.status.toUpperCase()}`,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 65,
            renderCell: renderCellExpand,
        },
        "d4992de9-92ab-4f9e-ba12-6c746e935611": {
            field: "colordescriptions",
            headerName: "COLOR DESCRIPTIONS",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 75,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "left",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 130,
            renderCell: (params) => params.row.stoneoldstatus === null ? params.value : (
                <Box
                    sx={{
                        "& .MuiBadge-badge": {
                            height: "0.75rem !important",
                            padding: "2px 4px !important",
                            fontSize: "8px",
                            top: "-1px",
                            right: "-20px",
                            backgroundColor: "#E5D9F2 !important",
                            color: "#624E88 !important",
                            fontWeight: "700"
                        },
                    }}
                >
                    <Badge
                        badgeContent={"TRANSIT"}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        {params.value}
                    </Badge>
                </Box>
            ),
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 140,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "77e48fa7-d118-4ab8-865b-9b85b32a004f": {
            field: "fancycolour",
            headerName: "F.COLOR",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "ccba9873-174e-4060-963a-9893c3625bb2": {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "7e356515-7cc5-4cd4-a1a4-a1b909b3b4e3": {
            field: "avgrapnetprice",
            headerName: "AVG RAP",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "a330b2b8-b9e4-40bb-bb7a-d0e83a717a74": {
            field: "avgdiscount",
            headerName: "AVG DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "1ac589a9-ff76-4b9a-9b6e-f0d7951dc0ca": {
            field: "avgprice",
            headerName: "AVG PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "49b8f961-fe52-48ca-afbe-ceab5d7dc740": {
            field: "avgtotal",
            headerName: "AVG TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: (params) => Utility.ConvertDateToAge(params.value),
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "cf694b75-ba5d-4a73-8fcf-e7566974c4b6":
        {
            field: "media",
            headerName: "MEDIA",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: (params) => (
                <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
                    <VideoLibraryTwoToneIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setStoneMediaURL(params.row.stonemediaurl);
                            setShowStoneMediaModal(true);
                        }} />
                </Tooltip>
            ),
        },
        "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
            field: "tableinc",
            headerName: "TABLE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
            field: "sideinc",
            headerName: "SIDE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "e993a4f7-7c50-4d83-abae-fccc03382742": {
            field: "h_and_a",
            headerName: "H&A",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
            field: "eyeclean",
            headerName: "EYE CLEAN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "c95089ee-166d-4783-817c-2638f6d40df1": {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
            field: "tableblack",
            headerName: "TABLE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
            field: "sideblack",
            headerName: "SIDE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "a1299782-d545-4d70-bcbc-7d576e396d64": {
            field: "length",
            headerName: "LENGTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
        },
        "dfd5488a-b600-4e75-9735-98c26f78dc84": {
            field: "strln",
            headerName: "STAR LENGTH",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "66dfbc62-477f-42f0-8097-56c5d8329156": {
            field: "lrhalf",
            headerName: "LR HALF",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
        },
        "e683145c-29dd-4d41-af1a-518ff80090d2": {
            field: "keytosymbols",
            headerName: "KEY TO SYMBOLS",
            headerAlign: "center",
            align: "center",
            width: 220,
        },
    };

    const rows = stockData;
    const columns = columnNames;

    // Color Scheme Drop Down Button
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseButton = () => {
        setAnchorEl(null);
    };

    const getSummaryData = () => {

        let data = [];
        if (selectionModel && selectionModel.length > 0) {
            data = stockData.filter((data) => selectionModel.includes(data.stoneid));
        } else {
            data = stockData;
        }

        let noOfStones = 0;
        let totalSize = 0;
        let totalAmount = 0;
        let total = 0;

        data.forEach(item => {
            noOfStones += 1;

            if (Utility.isValueValid(item.size)) {
                totalSize += Number(item.size);
            }

            if (Utility.isValueValid(item.total)) {
                totalAmount += Number(item.total);
            }

            if (
                Utility.isValueValid(item.price) &&
                Utility.isValueValid(item.discount) &&
                Utility.isValueValid(item.size)
            ) {
                const effectivePrice = (100 * Number(item.price)) / (100 + Number(item.discount));
                total += Number(item.size) * effectivePrice;
            }
        });

        return {
            ID: noOfStones,
            CTS: (Number(totalSize)).toFixed(2),
            DIS: (Number((100 - (totalAmount / total) * 100) * -1)).toFixed(2),
            PPC: (Number(totalAmount / totalSize)).toFixed(2),
            TOTAL: (Number(totalAmount)).toFixed(2),
        };

    }

    // Prepare Data Excel Export
    const getExcelData = (fullExcel) => {
        let data = [];

        if (selectionModel && selectionModel.length > 0) {
            data = stockData.filter((data) => selectionModel.includes(data.stoneid));
        } else {
            data = stockData;
        }

        const excelData = data.map(item => {

            return {
                REPORT: item.certificateid,
                SHAPE: item.shape,
                ID: item.stoneid,
                CTS: item.size,
                COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
                CLA: item.clarity,
                CUT: item.cut ? item.cut : " ",
                POL: item.pol,
                SYM: item.sym,
                FLR: item.flo,
                MEASUREMENTS: item.measurements,
                DEP: !item.depthpercent ? "" : (Number(item.depthpercent)).toFixed(2),
                "TABLE%": !item.tablepercent ? "" : (Number(item.tablepercent)).toFixed(2),
                "CR ANG": !item.crownangle ? "" : (Number(item.crownangle)).toFixed(2),
                LAB: item.lab,
                RAP: item.rapnetprice,
                DIS: parseFloat((Number(item.discount)).toFixed(2)),
                PPC: parseFloat((Number(item.price)).toFixed(2)),
                TOTAL: parseFloat((Number(item.total)).toFixed(2)),
                LOCATION: item.location,
                STATUS: item.status,
                KEYTOSYMBOLS: item.keytosymbols ? item.keytosymbols : " ",
                MEDIA: {
                    t: "s",
                    v: "Media URL",
                    l: {
                        Target: item.stonemediaurl,
                        Tooltip: "Click to open images & videos",
                    }, TOTAL: (Number(item.total)).toFixed(2),
                },
                status: item.status ? item.status : "",
                holdstonecustomerid: item.holdstonecustomerid,
                holdstoneuserid: item.holdstoneuserid,
                holdstonebrokerid: item.holdstonebrokerid,
                TABLEINC: item.tableinc ? item.tableinc : "",
                SIDEINC: item.sideinc ? item.sideinc : "",
                H_AND_A: item.h_and_a ? item.h_and_a : "",
                EYECLEAN: item.eyeclean ? item.eyeclean : "",
                TINGE: item.tinge ? item.tinge : "",
                MILKY: item.milky ? item.milky : "",
                OPEN: item.open ? item.open : "",
                LUSTER: item.luster ? item.luster : "",
                TABLEBLACK: item.tableblack ? "YES" : "NO",
                SIDEBLACK: item.sideblack ? "YES" : "NO",
            };
        });

        const calculatedRow = getSummaryData();

        //addding last row for summary data
        const lastRow = Object.fromEntries(
            Constants.STOCK_EXCEL_KEYS.map(key => [key, calculatedRow[key] || ""])
        );

        //addding last row for summary data
        excelData.push(lastRow);

        const exportColumns = fullExcel
            ? [...Constants.STOCK_EXCEL_COLUMNS, ...Constants.EXCEL_OPTIONAL_COLUMNS]
            : Constants.STOCK_EXCEL_COLUMNS;

        createExcelFile(excelData, null, exportColumns, "PI-Stock.xlsx");
    }

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {
                access ? <NoPermission /> : (
                    <Container maxWidth="xxl" className="background">
                        <Drawer
                            anchor="left"
                            open={drawerExpanded}
                            style={{ width: "50%" }}
                            onClose={() => setDrawerExpanded(!drawerExpanded)}>
                            <StockSearchCriteria
                                displaySearchCriteria={false}
                                stockCriteriaStored={stockCriteriaState}
                                handleSearch={handleSearch}
                                showSaveSearchAs={false}
                                setCustomerId={(data) => { }}
                                drawerClose={() => setDrawerExpanded(false)}
                                setFilters={(stockCriteriaStored) => setStockCriteriaState(stockCriteriaStored)}
                                readOnly={false}
                                enableReportNoSearchBox={false}
                            />
                        </Drawer>

                        {showStoneDetailModal ? (
                            <StoneDetailModal
                                show={showStoneDetailModal}
                                stoneId={stoneId}
                                handleClose={handleClose}
                            />
                        ) : null}

                        {showKapanDetailModal ? (
                            <KapanHistoryModal
                                show={showKapanDetailModal}
                                kapanId={kapanId}
                                close={() => setShowKapanDetailModal(false)}
                            />
                        ) : null}
                        {showStoneMediaModal ? (
                            <>
                                <StoneMediaModal
                                    show={showStoneMediaModal}
                                    stoneMediaURL={stoneMediaURL}
                                    handleClose={() => setShowStoneMediaModal(false)}
                                />
                            </>
                        ) : null}

                        {showPacketDetailModal ? (
                            <PacketHistoryModal
                                show={showPacketDetailModal}
                                packetId={packetId}
                                kapanId={kapanId}
                                close={handleClose}
                            />
                        ) : null}

                        <Box margin={"8px 0"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"} gap={"20px"}>
                            <Box width={"60%"} display={"flex"} gap={"20px"} alignItems={"center"}>
                                <Tooltip title="Show All Filters">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color='inherit'
                                        sx={{ backgroundColor: '#454545', color: 'white', '&:hover': { backgroundColor: '#454545' }, height: '46px' }}
                                        startIcon={<TuneIcon size="large" />}
                                        onClick={() => setDrawerExpanded(true)}
                                    >
                                        All Filters
                                    </Button>
                                </Tooltip>

                                <Box
                                    display={"flex"}
                                    alignItems={"center"}
                                    gap="10px"
                                    width={"20%"}
                                    sx={{
                                        "& .MuiOutlinedInput-input": {
                                            height: "13px",
                                        }
                                    }}
                                >
                                    <Typography m={0}>Date</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            className="picker"
                                            ampm={true}
                                            renderInput={(props) => (
                                                <TextField
                                                    fullWidth
                                                    {...props}
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                />
                                            )}
                                            value={date}
                                            inputFormat="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                                // Get the current time
                                                const now = dayjs();

                                                // Set startOfMonth to the first day of the selected month, but with the current system time
                                                const date = dayjs(newValue)
                                                    .hour(now.hour())
                                                    .minute(now.minute())
                                                    .second(now.second())
                                                    .millisecond(now.millisecond())
                                                    .toDate();

                                                setDate(date);
                                            }}
                                            sx={{
                                                width: "60%"
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "55%",
                                        gap: "10px",
                                    }}
                                >
                                    <Typography>Status</Typography>
                                    <Box
                                        sx={{
                                            alignSelf: "center",
                                            width: "66%",
                                        }}
                                    >
                                        <Select menuPlacement="auto"
                                            blurInputOnSelect={false}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                ...Constants.Styles,
                                                control: (base) => ({
                                                    ...base,
                                                    width: '24rem',
                                                    minHeight: "46px"
                                                }),
                                            }}
                                            closeMenuOnSelect={false}
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => option.value}
                                            isMulti
                                            options={stoneStatusOptions}
                                            value={stoneStatus}
                                            onChange={(e) => setStoneStatus(e)}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    width: "37%",
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: "black",
                                    fontWeight: 'normal',
                                    fontSize: Constants.defaultTableFontSize
                                }}
                            ><p style={{ margin: 0 }}>
                                    <span>No of Stones: </span> <b>{noOfSelectedStones}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                    <span>Total Weight: </span> <b>{selectedTotalSize}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                    <span>Total Price: </span> <b>{selectedtotalPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                    <span>Avg Price: </span> <b>{selectedavgPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                    <span>Avg Discount: </span> <b>{selectedAvgDiscount}</b></p>
                            </Box>

                            <div style={{ width: "3%", display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <div>
                                    <Box
                                        id="demo-customized-button"
                                        aria-controls={open ? 'demo-customized-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={open ? 'true' : undefined}
                                        variant="outlined"
                                        disableElevation
                                        onClick={handleClick}
                                        endIcon={<KeyboardArrowDownIcon />}
                                        sx={{
                                            color: '#454545',
                                            borderColor: '#454545',
                                            borderRadius: "4px",
                                            border: "1px solid black",
                                            padding: "5px 0 5px 5px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        <img
                                            src={colourchart}
                                            alt="Color Scheme"
                                            style={{
                                                height: '24px',
                                                width: '24px',
                                            }}
                                        />
                                        <KeyboardArrowDownIcon />
                                    </Box>
                                    <StyledMenu
                                        id="demo-customized-menu"
                                        MenuListProps={{
                                            'aria-labelledby': 'demo-customized-button',
                                            style: {
                                                maxHeight: 250
                                            },
                                        }}
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleCloseButton}
                                    >
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#F9F08D", borderRadius: 20, margin: 10 }}></span>NEW
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#7EAEEE", borderRadius: 20, margin: 10 }}></span>LAB
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#FCCD91", borderRadius: 20, margin: 10 }}></span>RE-LAB
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#FFFFCC", borderRadius: 20, margin: 10 }}></span>RE-CHECK
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#B8ACF6", borderRadius: 20, margin: 10 }}></span>RE-CHECK_SENT
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#388BFF", borderRadius: 20, margin: 10 }}></span>RE-CHECK_DONE
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#E0E88C", borderRadius: 20, margin: 10 }}></span>MIX
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#FBDEDE", borderRadius: 20, margin: 10 }}></span>AVAILABLE_APPROVAL_PENDING
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#000000", borderRadius: 20, margin: 10 }}></span>AVAILABLE
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#B6B9D6", borderRadius: 20, margin: 10 }}></span>HOLD
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#CAF7F6", borderRadius: 20, margin: 10 }}></span>MEMO_OUT
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#CCFF99", borderRadius: 20, margin: 10 }}></span>SELL
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#F797D2", borderRadius: 20, margin: 10 }}></span>SELL_SENT
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#26D4B9", borderRadius: 20, margin: 10 }}></span>SOLD
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#F59598", borderRadius: 20, margin: 10 }}></span>SELL_CANCELLED
                                        </MenuItem>
                                        <MenuItem onClick={handleCloseButton} disableRipple>
                                            <span style={{ height: "15px", width: "15px", backgroundColor: "#FEBD7D", borderRadius: 20, margin: 10 }}></span>AVAILABLE (IN_TRANSIT)
                                        </MenuItem>
                                    </StyledMenu>
                                </div>
                            </div>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: '78vh'
                            }}
                        >
                            <StyledDataGrid
                                disableRowSelectionOnClick={true}
                                localeText={{ noRowsLabel: "No Record Found" }}
                                sx={{ height: "78vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                rows={rows}
                                columns={columns}
                                // style={{ height: "25000pt" }}
                                sortModel={sortModel}
                                onRowSelectionModelChange={(newSelectionModel) => {
                                    setSelectionModel(newSelectionModel);
                                }}
                                onSortModelChange={(newModel) => setSortModel(newModel)}
                                getRowId={(row) => row.stoneid}
                                checkboxSelection={true}
                                loading={stockLoader}
                                selectionModel={selectionModel}
                                rowSelectionModel={selectionModel}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            locationid: false,
                                            stoneid: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: {
                                            debounceMs: 500,
                                        },
                                    },
                                }}
                            />
                            <Box sx={{
                                position: "absolute",
                                left: "0.4rem",
                                top: "1px"
                            }}>
                                <CommonStoneSelection title={"Enter multiple Stone Ids"} handleSelection={handleSelection} bgColor='#454545' buttonDisabled={rows.length === 0} />
                            </Box>
                            <Box style={{ position: "absolute", top: 5, right: "270px", display: "flex", alignItems: "center" }}>
                                {searchCriteria === "" ? (
                                    <Box />
                                ) : stockLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(searchCriteria, stockCriteriaState)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                                {!Utility.isReadAllowed(fullExcelPermission) ? (
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        color="secondary"
                                        // sx={{ ml: "5px", }}
                                        startIcon={<Download />}
                                        onClick={() => getExcelData(false)}
                                    >
                                        EXCEL
                                    </Button>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                            marginLeft: "5px",
                                            "& .MuiButton-startIcon": {
                                                marginRight: "0px !important",
                                                marginLeft: "-2px",
                                            },
                                            "& .MuiLoadingButton-loadingIndicator": { left: "7px" },
                                        }}
                                    >
                                        <LoadingButton
                                            id="demo-customized-export-menu"
                                            aria-controls={
                                                open1 ? "demo-customized-export-menu" : undefined
                                            }
                                            aria-haspopup="true"
                                            aria-expanded={open1 ? "true" : undefined}
                                            variant="outlined"
                                            disableElevation
                                            onClick={handleClick1}
                                            sx={{
                                                color: "#2e7d32",
                                                borderRadius: "4px",
                                                border: "1px solid rgba(46, 125, 50, 0.5)",
                                                padding: "2px 2px 2px 8px",
                                                cursor: "pointer",
                                            }}
                                            loadingPosition="start"
                                            startIcon={<TbTableExport size={18} />}
                                        >
                                            <span style={{ fontSize: "14px", marginLeft: "4px" }}>EXPORT</span>
                                            <KeyboardArrowDownIcon />
                                        </LoadingButton>

                                        <StyledMenuButtons
                                            id="demo-customized-export-menu"
                                            MenuListProps={{
                                                "aria-labelledby": "demo-customized-export-menu",
                                                style: {
                                                    maxHeight: 250,
                                                },
                                            }}
                                            anchorEl={anchorEl1}
                                            open={anchorEl1}
                                            onClose={() => handleCloseButton1()}
                                        >
                                            <MenuItem>
                                                {Utility.isReadAllowed(fullExcelPermission) &&
                                                    <Button
                                                        component="label"
                                                        variant="outlined"
                                                        size="small"
                                                        color="secondary"
                                                        sx={{
                                                            ml: "5px",
                                                            minWidth: buttonMinWidth,
                                                            justifyContent: "flex-start",
                                                            textAlign: "left",
                                                            "& .MuiButton-startIcon": {
                                                                marginRight: "1px",
                                                                marginLeft: "0px",
                                                            },
                                                        }}
                                                        startIcon={<Download />}
                                                        onClick={() => {
                                                            getExcelData(true);
                                                            handleCloseButton1()
                                                        }}
                                                    >
                                                        FULL EXCEL
                                                    </Button>
                                                }
                                            </MenuItem>
                                            <MenuItem>
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    size="small"
                                                    color="secondary"
                                                    sx={{
                                                        ml: "5px",
                                                        minWidth: buttonMinWidth,
                                                        justifyContent: "flex-start",
                                                        textAlign: "left",
                                                        "& .MuiButton-startIcon": {
                                                            marginRight: "1px",
                                                            marginLeft: "0px",
                                                        },
                                                    }}
                                                    startIcon={<Download />}
                                                    onClick={() => {
                                                        getExcelData(false);
                                                        handleCloseButton1()
                                                    }}
                                                >
                                                    EXCEL
                                                </Button>
                                            </MenuItem>
                                        </StyledMenuButtons>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Container>
                )}
        </div>
    );
};

export default StoneAsOnDate;